import React, { createContext, useContext, useReducer, useCallback } from 'react';

// Initial state
const initialState = {
  recommendations: [],
  loading: false,
  error: null,
  notification: null,
  selectedZoneId: null
};

// Action types
const RECOMMENDATION_ACTIONS = {
  FETCH_START: 'FETCH_START',
  FETCH_SUCCESS: 'FETCH_SUCCESS',
  FETCH_ERROR: 'FETCH_ERROR',
  CREATE_TASK_SUCCESS: 'CREATE_TASK_SUCCESS',
  DISMISS_RECOMMENDATION: 'DISMISS_RECOMMENDATION',
  SET_NOTIFICATION: 'SET_NOTIFICATION',
  CLEAR_NOTIFICATION: 'CLEAR_NOTIFICATION',
  SET_SELECTED_ZONE: 'SET_SELECTED_ZONE'
};

// Reducer function
const recommendationReducer = (state, action) => {
  switch (action.type) {
    case RECOMMENDATION_ACTIONS.FETCH_START:
      return {
        ...state,
        loading: true,
        error: null
      };
    case RECOMMENDATION_ACTIONS.FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        recommendations: action.payload,
        error: null
      };
    case RECOMMENDATION_ACTIONS.FETCH_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload
      };
    case RECOMMENDATION_ACTIONS.CREATE_TASK_SUCCESS:
      return {
        ...state,
        recommendations: state.recommendations.map(rec => 
          rec.id === action.payload.recommendationId 
            ? { ...rec, taskCreated: true } 
            : rec
        )
      };
    case RECOMMENDATION_ACTIONS.DISMISS_RECOMMENDATION:
      return {
        ...state,
        recommendations: state.recommendations.filter(rec => rec.id !== action.payload)
      };
    case RECOMMENDATION_ACTIONS.SET_NOTIFICATION:
      return {
        ...state,
        notification: {
          type: action.payload.type,
          message: action.payload.message
        }
      };
    case RECOMMENDATION_ACTIONS.CLEAR_NOTIFICATION:
      return {
        ...state,
        notification: null
      };
    case RECOMMENDATION_ACTIONS.SET_SELECTED_ZONE:
      return {
        ...state,
        selectedZoneId: action.payload
      };
    default:
      return state;
  }
};

// Create context
const RecommendationContext = createContext();

/**
 * Recommendation Context Provider component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export const RecommendationContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer(recommendationReducer, initialState);

  // Set notification
  const setNotification = useCallback((type, message, duration = 5000) => {
    dispatch({
      type: RECOMMENDATION_ACTIONS.SET_NOTIFICATION,
      payload: { type, message }
    });

    // Auto-clear notification after duration
    setTimeout(() => {
      dispatch({ type: RECOMMENDATION_ACTIONS.CLEAR_NOTIFICATION });
    }, duration);
  }, []);

  // Clear notification
  const clearNotification = useCallback(() => {
    dispatch({ type: RECOMMENDATION_ACTIONS.CLEAR_NOTIFICATION });
  }, []);

  // Set selected zone
  const setSelectedZone = useCallback((zoneId) => {
    dispatch({
      type: RECOMMENDATION_ACTIONS.SET_SELECTED_ZONE,
      payload: zoneId
    });
  }, []);

  // Load recommendations for a specific zone or all zones
  const loadRecommendations = useCallback(async (zoneId = null) => {
    dispatch({ type: RECOMMENDATION_ACTIONS.FETCH_START });
    
    try {
      // In a real app, this would call the API with the zone ID if provided
      // For now, we'll use mock data
      
      // Mock API call
      // const response = await api.get(`/recommendations${zoneId ? `?zoneId=${zoneId}` : ''}`);
      // const recommendations = response.data;
      
      // Mock data for development
      const recommendations = [
        {
          id: 'rec-1',
          title: 'Increase irrigation in North Field',
          description: 'Soil moisture is below optimal levels and forecast shows no rain in the next 48 hours.',
          type: 'irrigation',
          priority: 'high',
          zoneId: 'zone-1',
          timestamp: new Date().toISOString(),
          reasoning: 'Current soil moisture is at 58%, which is below the optimal range of 65-75% for Albion strawberries. Weather forecast indicates no precipitation for the next 48 hours with temperatures expected to rise to 26°C.',
          actionable: true
        },
        {
          id: 'rec-2',
          title: 'Apply nitrogen-rich fertilizer to South Field',
          description: 'Current nitrogen levels are 14% below optimal for Seascape variety at this growth stage.',
          type: 'nutrient',
          priority: 'medium',
          zoneId: 'zone-2',
          timestamp: new Date(Date.now() - 86400000).toISOString(), // Yesterday
          reasoning: 'Nitrogen levels in South Field are currently at 103 ppm, while the optimal range for Seascape strawberries at flowering stage is 120-140 ppm. Applying a nitrogen-rich fertilizer will help support flower and fruit development.',
          actionable: true
        },
        {
          id: 'rec-3',
          title: 'Monitor for aphids in all zones',
          description: 'Current weather conditions are favorable for aphid population growth.',
          type: 'pest',
          priority: 'low',
          zoneId: null, // Applies to all zones
          timestamp: new Date(Date.now() - 172800000).toISOString(), // 2 days ago
          reasoning: 'The combination of warm temperatures (22-25°C) and moderate humidity (60-70%) creates ideal conditions for aphid reproduction. Early detection can prevent significant crop damage.',
          actionable: true
        }
      ];
      
      // Filter by zone if specified
      const filteredRecommendations = zoneId 
        ? recommendations.filter(rec => rec.zoneId === zoneId || rec.zoneId === null)
        : recommendations;
      
      dispatch({
        type: RECOMMENDATION_ACTIONS.FETCH_SUCCESS,
        payload: filteredRecommendations
      });
      
      if (zoneId) {
        setSelectedZone(zoneId);
      }
      
      return filteredRecommendations;
    } catch (error) {
      console.error('Error loading recommendations:', error);
      const errorMessage = error.response?.data?.message || 'Failed to load recommendations';
      
      dispatch({
        type: RECOMMENDATION_ACTIONS.FETCH_ERROR,
        payload: errorMessage
      });
      
      setNotification('error', errorMessage);
      return [];
    }
  }, [setNotification, setSelectedZone]);

  // Create a task from a recommendation
  const createTaskFromRecommendation = useCallback(async (recommendation) => {
    try {
      // In a real app, this would call the API to create a task
      // For now, we'll just simulate success
      
      // Mock API call
      // const response = await api.post('/tasks', {
      //   title: recommendation.title,
      //   description: recommendation.description,
      //   priority: recommendation.priority,
      //   zoneId: recommendation.zoneId,
      //   dueDate: new Date().toISOString().split('T')[0], // Today
      //   recommendationId: recommendation.id
      // });
      
      // Simulate successful task creation
      dispatch({
        type: RECOMMENDATION_ACTIONS.CREATE_TASK_SUCCESS,
        payload: { recommendationId: recommendation.id }
      });
      
      setNotification('success', 'Task created successfully');
      return true;
    } catch (error) {
      console.error('Error creating task:', error);
      const errorMessage = error.response?.data?.message || 'Failed to create task';
      
      setNotification('error', errorMessage);
      return false;
    }
  }, [setNotification]);

  // Dismiss a recommendation
  const dismissRecommendation = useCallback(async (recommendationId) => {
    try {
      // In a real app, this would call the API to dismiss the recommendation
      // For now, we'll just update the local state
      
      // Mock API call
      // await api.post(`/recommendations/${recommendationId}/dismiss`);
      
      dispatch({
        type: RECOMMENDATION_ACTIONS.DISMISS_RECOMMENDATION,
        payload: recommendationId
      });
      
      setNotification('info', 'Recommendation dismissed');
      return true;
    } catch (error) {
      console.error('Error dismissing recommendation:', error);
      const errorMessage = error.response?.data?.message || 'Failed to dismiss recommendation';
      
      setNotification('error', errorMessage);
      return false;
    }
  }, [setNotification]);

  // Context value
  const contextValue = {
    state,
    loadRecommendations,
    createTaskFromRecommendation,
    dismissRecommendation,
    setSelectedZone,
    setNotification,
    clearNotification
  };

  return (
    <RecommendationContext.Provider value={contextValue}>
      {children}
    </RecommendationContext.Provider>
  );
};

// Custom hook for using the recommendation context
export const useRecommendation = () => {
  const context = useContext(RecommendationContext);
  
  if (!context) {
    throw new Error('useRecommendation must be used within a RecommendationContextProvider');
  }
  
  return context;
};

export default RecommendationContext;
